/**
 * WebhookSubscriptionsPage.jsx — Customer-facing webhook subscription manager.
 * Backend: /webhooks/list, /webhooks/subscribe, /webhooks/update, /webhooks/delete, /webhooks/test
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Tooltip,
  Popover,
  Popconfirm,
  message as antdMessage,
  Empty,
  Skeleton,
} from 'antd';
import { Skeleton as BoneSkeleton } from 'boneyard-js/react';
import {
  PlusOutlined,
  ReloadOutlined,
  ApiOutlined,
  ExperimentOutlined,
  EditOutlined,
  DeleteOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import webhookApi from '../../../../../api/webhook.api';

const EVENT_OPTIONS = [
  { value: 'message.queued', label: 'message.queued' },
  { value: 'message.sent', label: 'message.sent' },
  { value: 'message.delivered', label: 'message.delivered' },
  { value: 'message.read', label: 'message.read' },
  { value: 'message.failed', label: 'message.failed' },
  { value: 'message.received', label: 'message.received' },
  { value: 'campaign.audience.fetch.started', label: 'campaign.audience.fetch.started' },
  { value: 'campaign.audience.fetch.completed', label: 'campaign.audience.fetch.completed' },
  { value: 'campaign.audience.fetch.failed', label: 'campaign.audience.fetch.failed' },
  { value: 'campaign.completed', label: 'campaign.completed' },
  { value: 'campaign.refunded', label: 'campaign.refunded' },
];

const TableRowSkeleton = () => (
  <div style={{ padding: '4px 0' }}>
    {[...Array(5)].map((_, i) => (
      <div key={i} style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: '1px solid #f0f0f0', alignItems: 'center' }}>
        <Skeleton.Input active size="small" style={{ width: 160, height: 14 }} />
        <Skeleton.Input active size="small" style={{ width: 200, height: 14 }} />
        <Skeleton.Input active size="small" style={{ width: 80, height: 14 }} />
        <Skeleton.Input active size="small" style={{ width: 60, height: 14 }} />
        <Skeleton.Input active size="small" style={{ width: 80, height: 14 }} />
      </div>
    ))}
  </div>
);

export default function WebhookSubscriptionsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await webhookApi.listSubscriptions();
      const body = res.data;
      const items = body?.data?.items || body?.data || body?.entity?.subscriptions || body?.entity?.items || body?.items || [];
      // Normalize: API returns subscription_id / is_active; table expects id / enabled
      const normalized = (Array.isArray(items) ? items : []).map((s) => ({
        ...s,
        id: s.id ?? s.subscription_id,
        enabled: s.enabled ?? s.is_active,
      }));
      setRows(normalized);
    } catch (err) {
      console.error('Failed to load subscriptions', err);
      antdMessage.error('Could not load webhook subscriptions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ enabled: true, events: ['message.delivered', 'message.failed'] });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      url: row.url,
      events: row.events || [],
      secret: '', // never pre-fill secrets
      enabled: row.enabled !== false,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        const patch = { url: values.url, events: values.events, enabled: values.enabled };
        if (values.secret) patch.secret = values.secret;
        await webhookApi.updateSubscription(editing.id, patch);
        antdMessage.success('Subscription updated.');
      } else {
        await webhookApi.createSubscription(values);
        antdMessage.success('Subscription created.');
      }
      setModalOpen(false);
      fetchData();
    } catch (err) {
      if (err?.errorFields) return;
      antdMessage.error(err.response?.data?.message || 'Failed to save subscription.');
    }
  };

  const handleDelete = async (row) => {
    try {
      await webhookApi.deleteSubscription(row.id);
      antdMessage.success('Subscription deleted.');
      fetchData();
    } catch (err) {
      antdMessage.error(err.response?.data?.message || 'Delete failed.');
    }
  };

  const handleTest = async (row) => {
    try {
      await webhookApi.testSubscription(row.id);
      antdMessage.success('Test event fired. Check delivery log.');
    } catch (err) {
      antdMessage.error(err.response?.data?.message || 'Test fire failed.');
    }
  };

  const columns = [
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      ellipsis: true,
      render: (url) => <code style={{ fontSize: 12 }}>{url}</code>,
    },
    {
      title: 'Events',
      dataIndex: 'events',
      key: 'events',
      width: 280,
      render: (events) => {
        const safeEvents = Array.isArray(events) ? events : [];
        const visibleEvents = safeEvents.slice(0, 3);
        const remainingEvents = safeEvents.slice(3);

        return (
          <Space size={6} wrap>
            {visibleEvents.map((eventName) => (
              <Tag
                key={eventName}
                style={{
                  borderRadius: 6,
                  padding: '2px 8px',
                  fontSize: 12,
                  borderColor: '#dbeafe',
                  background: '#f8fbff',
                  color: '#1e293b',
                }}
              >
                {eventName}
              </Tag>
            ))}

            {remainingEvents.length > 0 && (
              <Popover
                trigger="click"
                placement="topLeft"
                overlayStyle={{ maxWidth: 320 }}
                content={
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxWidth: 300 }}>
                    {remainingEvents.map((eventName) => (
                      <Tag key={eventName} style={{ margin: 0, borderRadius: 6, fontSize: 12 }}>
                        {eventName}
                      </Tag>
                    ))}
                  </div>
                }
              >
                <Tag
                  style={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    borderRadius: 6,
                    margin: 0,
                    borderColor: '#cbd5e1',
                    background: '#f8fafc',
                    color: '#334155',
                    fontWeight: 600,
                    padding: '2px 8px',
                  }}
                >
                  +{remainingEvents.length}
                </Tag>
              </Popover>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 100,
      render: (enabled, row) =>
        enabled === false ? (
          <Tag color="red">Disabled</Tag>
        ) : row.consecutive_failures > 0 ? (
          <Tooltip title={`${row.consecutive_failures} consecutive failures`}>
            <Tag color="orange">Degraded</Tag>
          </Tooltip>
        ) : (
          <Tag color="green">Active</Tag>
        ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 130,
      render: (v) => (v ? dayjs(v).format('DD MMM YYYY') : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 240,
      fixed: 'right',
      render: (_, row) => (
        <Space size={4}>
          <Tooltip title="Test fire">
            <Button size="small" icon={<ExperimentOutlined />} onClick={() => handleTest(row)} />
          </Tooltip>
          <Tooltip title="Delivery log">
            <Button
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => navigate(`/dashboard/webhooks/${row.id}/deliveries`)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          </Tooltip>
          <Popconfirm title="Delete this subscription?" onConfirm={() => handleDelete(row)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '0 24px 24px' }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: '1.45rem', fontWeight: 700, color: '#0f172a', margin: '0 0 4px', letterSpacing: '-0.03em', lineHeight: 1.2 }}>
          Webhooks
        </h2>
        <span style={{ color: '#6b7280', fontSize: 13 }}>Manage webhook subscriptions to receive real-time message events on your server.</span>
      </div>
      <Card
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>Refresh</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} style={{ background: '#0036b0', borderColor: '#0036b0' }}>New Subscription</Button>
          </Space>
        }
      >
        <BoneSkeleton name="webhook-subscriptions" loading={loading && rows.length === 0} fallback={<TableRowSkeleton />}>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={rows}
            loading={loading && rows.length > 0}
            scroll={{ x: 900 }}
            locale={{
              emptyText: (
                <Empty description="No webhook subscriptions yet. Create one to receive real-time message events on your server." />
              ),
            }}
          />
        </BoneSkeleton>
      </Card>

      <Modal
        title={editing ? 'Edit Subscription' : 'Create Subscription'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        width={520}
        centered
        footer={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 500 }}>Enabled</span>
              <Switch
                checked={form.getFieldValue('enabled') !== false}
                onChange={(checked) => form.setFieldsValue({ enabled: checked })}
              />
            </div>
            <Space>
              <Button onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button type="primary" onClick={handleSave} style={{ background: '#0036b0', borderColor: '#0036b0' }}>
                {editing ? 'Save' : 'Create'}
              </Button>
            </Space>
          </div>
        }
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="url"
            label="Webhook URL"
            rules={[
              { required: true, message: 'URL is required' },
              { type: 'url', message: 'Enter a valid URL' },
            ]}
          >
            <Input placeholder="https://your-server.com/messageos/webhook" />
          </Form.Item>
          <Form.Item
            name="events"
            label="Subscribed Events"
            rules={[{ required: true, message: 'Select at least one event' }]}
          >
            <Select
              mode="multiple"
              placeholder="Pick events to subscribe to"
              options={EVENT_OPTIONS}
            />
          </Form.Item>
          <Form.Item
            name="secret"
            label={editing ? 'Signing Secret (leave blank to keep current)' : 'Signing Secret'}
            extra="Used for HMAC-SHA256 signature on every delivery. Min 32 chars recommended."
            rules={editing ? [] : [{ required: true, message: 'Secret is required' }, { min: 16, message: 'Min 16 chars' }]}
          >
            <Input.Password placeholder="Generate a strong random secret" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
